import React, { useEffect, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';
import { getSearchResultsCardRenderSizes } from './searchResultsPanelHelpers';
import { pageSizeFromGridElement } from './searchPageSize';

// Layout effect on the client so the measured page size is set before the
// route's mount loadData. useEffect on the server avoids the SSR warning.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @param {Function} [props.onPageSize] - Called with the page size the visible grid can fill
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
    onPageSize,
  } = props;
  const gridRef = useRef(null);
  const paginationRef = useRef(null);

  useIsomorphicLayoutEffect(() => {
    if (!onPageSize || isMapVariant || typeof window === 'undefined') {
      return undefined;
    }
    const grid = gridRef.current;
    if (!grid) {
      return undefined;
    }

    let timer = null;
    const measure = () => {
      const size = pageSizeFromGridElement(grid, paginationRef.current, window);
      if (size) {
        onPageSize(size);
      }
    };
    const schedule = delay => {
      clearTimeout(timer);
      timer = setTimeout(measure, delay);
    };

    // Measure before paint so the route's mount loadData can use this size.
    // Later resizes are debounced so dragging the window does not refetch every frame.
    measure();
    const onResize = () => schedule(150);
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(onResize);
      observer.observe(grid);
    }
    window.addEventListener('resize', onResize);

    return () => {
      clearTimeout(timer);
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('resize', onResize);
    };
  }, [onPageSize, isMapVariant, listings.length, pagination?.totalPages]);
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = getSearchResultsCardRenderSizes(isMapVariant);

  return (
    <div className={classes}>
      <ul ref={gridRef} className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listings.map(l => (
          <li key={l.id.uuid} className={css.resultItem}>
            <ListingCard
              className={css.listingCard}
              listing={l}
              renderSizes={cardRenderSizes}
              setActiveListing={setActiveListing}
            />
          </li>
        ))}
        {props.children}
      </ul>
      {paginationLinks ? <div ref={paginationRef}>{paginationLinks}</div> : null}
    </div>
  );
};

export default SearchResultsPanel;
